/**
 * ClickPay Suggests' own queue — the rules behind the Queued tab (PR-B, owner
 * feedback #100 and #102).
 *
 * Owner decision: Suggests must not feed Rules > Approvals — that tab is for
 * rule automation. A bid / budget / placement suggestion that is staged now
 * waits as ONE pending manual edit whose note starts with SUGGESTS_NOTE_PREFIX,
 * and the Queued tab (components/SuggestsQueuePanel.tsx) applies it through the
 * same audited endpoint the Pending edits tray uses. An AMC audience is an
 * automation proposal and still goes to Rules > Approvals; negate / harvest /
 * dayparting are not staged at all — each has its own action.
 *
 * Everything here is pure (no React, no fetch) so the decisions are testable
 * on their own: suggestsQueue.test.ts.
 */
import {
  apiEntity,
  type AppliedEditDetail, type ApplyBatchManualEditsResult, type ManualEdit,
  type RecommendationKind, type StageResult,
} from './api'
import { fmtMoney } from './format'

/** The note every Suggests manual edit starts with. TWIN of
 *  backend/recs/staging.SUGGESTS_NOTE_PREFIX — tests/test_suggests_queue_parity.py
 *  reads this line and fails if the two ever differ. */
export const SUGGESTS_NOTE_PREFIX = 'ClickPay Suggests:'

/** Kinds that queue as a manual edit (and can Submit). TWIN of
 *  backend/recs/staging.SUBMITTABLE_KINDS (same parity test). */
export const SUBMITTABLE_KINDS: ReadonlySet<RecommendationKind> = new Set<RecommendationKind>([
  'bid_down', 'bid_up', 'budget_up', 'budget_down', 'placement',
])

/** Kinds that are automation proposals — they go to Rules > Approvals. TWIN of
 *  backend/recs/staging.AUTOMATION_PROPOSAL_KINDS. */
export const AUTOMATION_PROPOSAL_KINDS: ReadonlySet<RecommendationKind> = new Set<RecommendationKind>([
  'audience',
])

/** Kinds that are not staged anywhere — each has its own action. TWIN of
 *  backend/recs/staging.NON_STAGING_KINDS. */
export const NON_STAGING_KINDS: ReadonlySet<RecommendationKind> = new Set<RecommendationKind>([
  'dayparting', 'negate', 'harvest',
])

/** What the Stage control on a suggestion does:
 *  'queue'   — "Stage": add it to the Queued tab;
 *  'propose' — "Propose automation": send it to Rules > Approvals;
 *  null      — no Stage control at all (Negate… / Harvest… / the editor). */
export type StageAction = 'queue' | 'propose' | null

export function stageActionFor(kind: RecommendationKind): StageAction {
  if (SUBMITTABLE_KINDS.has(kind)) return 'queue'
  if (AUTOMATION_PROPOSAL_KINDS.has(kind)) return 'propose'
  return null
}

/** How many queued rows the Queued tab reads at once (the endpoint's cap). */
export const QUEUE_READ_LIMIT = 1000

/** Rows per apply-batch request. The server applies one change at a time —
 *  about 0.4 s each on prod (2026-09-17: p50 0.38 s, p90 0.45 s between
 *  consecutive applies).
 *
 *  25, not 100 (PR-B review #5): 100 × 0.4 s ≈ 45 s is not "well inside" a
 *  100 s edge timeout — it is under half of it with no margin for a slow
 *  Amazon call, and every row past the timeout is a write we cannot confirm.
 *  25 is ~10 s per request, a quarter of the exposure, and a progress update
 *  four times as often. The cost is more round trips, which are free next to
 *  an unconfirmed live write. */
export const APPROVE_ALL_CHUNK = 25

/** After a request FAILS, the ids in that chunk may still be applying on the
 *  server. These bound the wait before Approve all is offered again — see
 *  `settleUnconfirmed`. */
export const UNCONFIRMED_POLL_ATTEMPTS = 10
export const UNCONFIRMED_POLL_MS = 3_000

/** Seconds per change used for the "how long will this take" sentence —
 *  rounded up from the measured ~0.4 s so the estimate is not optimistic. */
export const SECONDS_PER_CHANGE = 0.5

export function chunk<T>(items: readonly T[], size: number): T[][] {
  const n = Math.max(1, Math.floor(size))
  const out: T[][] = []
  for (let i = 0; i < items.length; i += n) out.push(items.slice(i, i + n))
  return out
}

/** Plain-English wall time for applying `count` changes one at a time. */
export function approveAllDurationLabel(count: number): string {
  const seconds = count * SECONDS_PER_CHANGE
  if (seconds < 60) return 'under a minute'
  const minutes = Math.ceil(seconds / 60)
  return `about ${minutes} minute${minutes === 1 ? '' : 's'}`
}

/** The suggestion's own words: the note minus the Suggests prefix. */
export function queueTitle(notes: string | null | undefined): string {
  if (!notes) return ''
  return notes.startsWith(SUGGESTS_NOTE_PREFIX)
    ? notes.slice(SUGGESTS_NOTE_PREFIX.length).trim()
    : notes
}

/** Human name of the attribute a queued edit changes. */
export function queueFieldLabel(entityType: string, field: string): string {
  if (field === 'bid') return 'Bid'
  if (field === 'budget') return 'Daily budget'
  if (field === 'modifier') return entityType === 'placement' ? 'Placement bid boost' : 'Bid boost'
  if (field === 'state') return 'Status'
  return field
}

/** A queued value the way the rest of the app shows it: bids and budgets in
 *  dollars, a placement modifier (a decimal multiplier, 0.25 = +25%) as a
 *  percentage boost. Anything unparseable is shown as stored. */
export function formatQueueValue(field: string, raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '—'
  const v = Number(raw)
  if (!Number.isFinite(v)) return raw
  if (field === 'bid' || field === 'budget') return fmtMoney(v)
  if (field === 'modifier') return `+${Math.round(v * 100)}%`
  return raw
}

// ── Approve all ─────────────────────────────────────────────────────────────

/** The outcome of an Approve all (or a single Apply), counted per edit exactly
 *  the way the Pending edits tray counts a push (PendingTray.tsx onConfirmPush):
 *   applied        — the change reached Amazon;
 *   stagedNoWrites — both write switches were off, nothing reached Amazon;
 *   failed         — the server tried and refused or errored (see failures);
 *   valueChanged   — the live value no longer matched the one this row was
 *                    queued from, so the server refused it and sent NOTHING to
 *                    Amazon. Counted apart from `failed` because nothing is
 *                    broken: the change is simply out of date;
 *   unconfirmed    — the REQUEST for that chunk failed (network, timeout), so
 *                    some of its rows may still have gone through — the
 *                    refreshed list shows what is still waiting;
 *   notSent        — later chunks never sent, because an earlier request
 *                    failed (stopping beats hammering a server that is down). */
export type QueueTally = {
  applied: number
  stagedNoWrites: number
  failed: number
  valueChanged: number
  unconfirmed: number
  notSent: number
  appliedDetail: AppliedEditDetail[]
  failures: { id: number; error: string }[]
  stale: { id: number; error: string }[]
  requestError: string | null
  /** Ids from a failed request that were STILL pending when polling gave up —
   *  the server may or may not have finished them. */
  stillPending: number[]
}

export function emptyTally(): QueueTally {
  return {
    applied: 0, stagedNoWrites: 0, failed: 0, valueChanged: 0,
    unconfirmed: 0, notSent: 0,
    appliedDetail: [], failures: [], stale: [], requestError: null,
    stillPending: [],
  }
}

/** Fold one apply-batch response into `tally`, for the edits that were sent. */
export function tallyApplyResults(
  tally: QueueTally,
  sent: readonly ManualEdit[],
  response: ApplyBatchManualEditsResult,
): QueueTally {
  const outcome = new Map<number, { status: string; error: string | null }>()
  for (const res of response.results) {
    const status = res.result?.status ?? (res.ok ? 'applied' : 'failed')
    outcome.set(res.id, { status, error: res.result?.error_msg ?? res.error ?? null })
  }
  for (const edit of sent) {
    const o = outcome.get(edit.id) ?? { status: 'failed', error: 'no result returned' }
    if (o.status === 'applied') {
      tally.applied++
      tally.appliedDetail.push({
        entity_type: edit.entity_type, entity_id: edit.entity_id,
        field: edit.field, new_value: edit.new_value,
      })
    } else if (o.status === 'staged_no_writes') {
      tally.stagedNoWrites++
    } else if (o.status === 'stale' || o.status === 'value_changed') {
      // 'stale' since #69/#99 (the row is retired as superseded);
      // 'value_changed' is what a pre-#69 backend sends.
      tally.valueChanged++
      tally.stale.push({ id: edit.id, error: o.error ?? 'The value changed since this was suggested' })
    } else {
      tally.failed++
      tally.failures.push({ id: edit.id, error: o.error ?? 'apply failed' })
    }
  }
  return tally
}

const _sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/** After a request error, wait until the server is demonstrably done with that
 *  chunk — i.e. none of its ids is still 'pending'.
 *
 *  The request failing does NOT mean the server stopped: apply-batch applies
 *  one row at a time and keeps going after the client has given up. Re-enabling
 *  "Approve all" straight away is what lets a second press re-push rows the
 *  first press is still applying. Returns the ids that were STILL unsettled
 *  when we ran out of attempts (the honest "we don't know" set). `sleep` is
 *  injectable so tests don't wait in real time.
 *
 *  `listUnsettledIds` must report BOTH pending and 'applying' rows — a row the
 *  server is mid-apply on is the least settled thing there is. */
export async function settleUnconfirmed(
  ids: readonly number[],
  listUnsettledIds: () => Promise<number[]>,
  opts?: { attempts?: number; intervalMs?: number; sleep?: (ms: number) => Promise<void> },
): Promise<number[]> {
  const attempts = opts?.attempts ?? UNCONFIRMED_POLL_ATTEMPTS
  const intervalMs = opts?.intervalMs ?? UNCONFIRMED_POLL_MS
  const sleep = opts?.sleep ?? _sleep
  const want = new Set(ids)
  if (want.size === 0) return []
  let still: number[] = [...want]
  for (let i = 0; i < attempts; i++) {
    await sleep(intervalMs)
    let unsettled: number[]
    try {
      unsettled = await listUnsettledIds()
    } catch {
      continue // a failed poll is not proof of anything — try again
    }
    still = unsettled.filter(id => want.has(id))
    if (still.length === 0) return []
  }
  return still
}

/** Apply `edits` in chunks of APPROVE_ALL_CHUNK, one request at a time (the
 *  server applies sequentially anyway; parallel requests would only race each
 *  other for the same rate limit). Stops at the first request that fails —
 *  and, before returning, waits for that chunk to stop being pending
 *  (`settleUnconfirmed`), so the caller only re-enables Approve all once the
 *  server is no longer working on rows this run already sent. */
export async function approveAllInChunks(
  edits: readonly ManualEdit[],
  applyBatch: (ids: number[]) => Promise<ApplyBatchManualEditsResult>,
  onProgress?: (done: number, total: number) => void,
  listUnsettledIds?: () => Promise<number[]>,
  settleOpts?: { attempts?: number; intervalMs?: number; sleep?: (ms: number) => Promise<void> },
): Promise<QueueTally> {
  const tally = emptyTally()
  const chunks = chunk(edits, APPROVE_ALL_CHUNK)
  let done = 0
  for (let i = 0; i < chunks.length; i++) {
    const part = chunks[i]
    try {
      const response = await applyBatch(part.map(e => e.id))
      tallyApplyResults(tally, part, response)
    } catch (e) {
      tally.unconfirmed += part.length
      tally.notSent = chunks.slice(i + 1).reduce((n, c) => n + c.length, 0)
      tally.requestError = e instanceof Error ? e.message : String(e)
      if (listUnsettledIds) {
        tally.stillPending = await settleUnconfirmed(
          part.map(p => p.id), listUnsettledIds, settleOpts)
      }
      break
    }
    done += part.length
    onProgress?.(done, edits.length)
  }
  return tally
}

// ── the two calls the Queued tab makes ──────────────────────────────────────

/** The Queued tab's list: every operator's PENDING manual edits whose note
 *  starts with SUGGESTS_NOTE_PREFIX (GET /api/manual-edits?notes_prefix=). */
export function listQueuedSuggestions() {
  return apiEntity.listManualEdits({
    status: 'pending', scope: 'all', notes_prefix: SUGGESTS_NOTE_PREFIX, limit: QUEUE_READ_LIMIT,
  })
}

/** Ids of queued rows the server has NOT finished with — pending, plus the ones
 *  it is mid-apply on.
 *
 *  'applying' is the status a row wears between being claimed and its outcome
 *  (operators_api). Polling only 'pending' after a request error said "settled"
 *  about exactly the rows still being written — the case the wait exists for
 *  (round-2 review MINOR-8). Two reads rather than one: the endpoint takes a
 *  single status, and 'all' would drag in the applied history. */
export async function listUnsettledQueuedIds(): Promise<number[]> {
  const [pending, applying] = await Promise.all([
    listQueuedSuggestions(),
    apiEntity.listManualEdits({
      status: 'applying', scope: 'all', notes_prefix: SUGGESTS_NOTE_PREFIX,
      limit: QUEUE_READ_LIMIT,
    }),
  ])
  return [...pending.items, ...applying.items].map(e => e.id)
}

/** The Queued tab's second list: recent FAILED Suggests rows. They leave every
 *  'pending' list the moment they fail, so without this they show nowhere and
 *  the suggestion behind them quietly reappears (PR-B review #8). */
export function listFailedQueuedSuggestions() {
  return apiEntity.listManualEdits({
    status: 'failed', scope: 'all', notes_prefix: SUGGESTS_NOTE_PREFIX,
    limit: FAILED_READ_LIMIT,
  })
}

/** How many recent failures the Queued tab lists. */
export const FAILED_READ_LIMIT = 50

/** Apply / Approve all: `edits` through POST /api/manual-edits/apply-batch —
 *  the Pending edits tray's audited path — in chunks, attributed to `operator`. */
export function approveQueuedSuggestions(
  operator: string,
  edits: readonly ManualEdit[],
  onProgress?: (done: number, total: number) => void,
): Promise<QueueTally> {
  return approveAllInChunks(
    edits,
    ids => apiEntity.applyBatchManualEdits(operator, ids),
    onProgress,
    listUnsettledQueuedIds,
  )
}

/** One plain-English sentence for an Approve all / Apply outcome. */
export function describeTally(t: QueueTally): string {
  const parts: string[] = []
  if (t.applied) parts.push(`${t.applied} applied to Amazon`)
  if (t.stagedNoWrites) parts.push(`${t.stagedNoWrites} recorded with live writes off (nothing reached Amazon)`)
  if (t.valueChanged) {
    parts.push(`${t.valueChanged} skipped — the value changed since ${t.valueChanged === 1 ? 'it was' : 'they were'} queued`)
  }
  if (t.failed) parts.push(`${t.failed} failed`)
  if (t.unconfirmed) parts.push(`${t.unconfirmed} not confirmed — the request failed; the list shows what is still waiting`)
  if (t.notSent) parts.push(`${t.notSent} not sent`)
  return parts.length ? parts.join(' · ') : 'Nothing to apply'
}

/** Did anything go wrong? (Drives the toast's tone.) A stale row is a problem
 *  the operator has to decide about, so it counts. */
export function tallyHasProblems(t: QueueTally): boolean {
  return t.failed > 0 || t.valueChanged > 0 || t.unconfirmed > 0 || t.notSent > 0
}

// ── Stage outcome copy ───────────────────────────────────────────────────────

/** One plain-English sentence for what a Stage (or Stage selected) did —
 *  where every suggestion went, so a toast never says "see Approvals" about a
 *  row that is in the Queued tab. */
export function describeStageResults(staged: readonly StageResult[]): string {
  const queued = staged.filter(s => s.queue === 'manual_edits' && !s.already_pending).length
  const alreadyQueued = staged.filter(
    s => s.queue === 'manual_edits' && s.already_pending && s.in_suggests_queue !== false).length
  const alreadyInTray = staged.filter(
    s => s.queue === 'manual_edits' && s.already_pending && s.in_suggests_queue === false).length
  const proposed = staged.filter(s => s.queue === 'approvals').length
  const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)
  const parts: string[] = []
  if (queued) parts.push(`${queued} ${plural(queued, 'suggestion', 'suggestions')} queued — see the Queued tab`)
  if (alreadyQueued) parts.push(`${alreadyQueued} ${plural(alreadyQueued, 'was', 'were')} already in the Queued tab`)
  if (alreadyInTray) {
    parts.push(`${alreadyInTray} already ${plural(alreadyInTray, 'has', 'have')} a change waiting in the Pending changes tray`)
  }
  if (proposed) {
    parts.push(`${proposed} ${plural(proposed, 'automation', 'automations')} proposed — review in Rules › Approvals`)
  }
  return parts.length ? parts.join(' · ') : 'Nothing was queued'
}
